import dayjs from 'dayjs';
import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AllocationSearchAndFilter from '../AllocationSearchAndFilter';
import AllocationTable from '../AllocationTable';
import ExpenseGroupSettingsModal from 'components/settings/ExpenseGroups/SettingsModal';
import styles from './index.module.scss';
import { Typography, Tooltip } from 'antd';
import {
	getExpenseAllocationAction,
	fetchExpenseGroupAllocationsAction,
} from 'redux/action/expenseGroupAllocationAction';
import { getOneOffExpenseAllocationAction } from 'redux/action/oneOffAction';
import {
	clearAllocationData,
	resetAllocationList,
} from 'redux/slice/expenseAllocationSlice';
import OneOffSettingsModal from '../OneOffSettingsModal';
import { AppDispatch } from 'redux/store';

const { Text } = Typography;

interface ExpenseAllocationMainProps {
	selectedExpenseGroup: string;
	onExpenseGroupChange: (expenseGroupId: string) => void;
	expenseGroupOptions: Array<{ value: string; label: string }>;
	expenseGroupsLoading: boolean;
	handlePayPeriodId?: (value: string) => void;
	searchValue?: string;
	setSearchValue?: (v: string) => void;
	filterValue?: any;
	setFilterValue?: (v: any) => void;
	sortBy?: string | null;
	setSortBy?: (v: string | null) => void;
	sortType?: string | null;
	setSortType?: (v: string | null) => void;
	isExpenseAllocation?: string;
	onScreenChange?: () => void;
	isExpanded?: boolean;
	dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
	onSwitchToJournalEntries?: () => void;
}
export default function ExpenseAllocationMain(
	props: ExpenseAllocationMainProps
) {
	const {
		selectedExpenseGroup,
		expenseGroupOptions,
		dateRange,
		onExpenseGroupChange,
		onSwitchToJournalEntries,
	} = props;
	const dispatch = useDispatch<AppDispatch>();

	// Settings modal state
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [selectedForSettings, setSelectedForSettings] = useState<{
		id: string;
		name: string;
	} | null>(null);

	const {
		allocationData,
		allocationLoading,
		hasMore,
		isLoadingMore,
		pagination,
		grandTotal,
	} = useSelector((state: any) => state.expenseAllocation);

	const { currentItems: configuredAllocations, temporaryConfig } = useSelector(
		(state: any) => state.expenseGroupAllocation
	);

	// Get expense groups from Redux to access the selected expense group's configuration
	const { data: expenseGroups } = useSelector(
		(state: any) => state.expenseGroups
	);

	// Get one-off configurations for One-Off mode
	const { oneOffConfigurations } = useSelector((state: any) => state.oneOff);

	// Check if One-Off mode is ON
	const isOneOffMode = selectedExpenseGroup === 'all';

	// Settings modal state for One-Off mode
	const [isOneOffSettingsOpen, setIsOneOffSettingsOpen] = useState(false);

	// Settings modal functions
	const openSettings = () => {
		if (!selectedExpenseGroup) return;

		if (isOneOffMode) {
			// Open One-Off settings modal
			setIsOneOffSettingsOpen(true);
		} else {
			// Open normal expense group settings modal
			const selectedLabel =
				expenseGroupOptions.find((e: any) => e.value === selectedExpenseGroup)
					?.label || 'Unknown';
			setSelectedForSettings({ id: selectedExpenseGroup, name: selectedLabel });
			setIsSettingsOpen(true);
		}
	};

	const closeSettings = () => {
		setIsSettingsOpen(false);
		setSelectedForSettings(null);
	};

	const closeOneOffSettings = () => {
		setIsOneOffSettingsOpen(false);
	};

	// Fetch allocation configuration to check if allocations are set up
	const fetchAllocationConfiguration = useCallback(async () => {
		if (selectedExpenseGroup && selectedExpenseGroup !== 'all') {
			dispatch(
				fetchExpenseGroupAllocationsAction({
					expenseGroupId: selectedExpenseGroup,
				})
			);
		}
	}, [selectedExpenseGroup, dispatch]);

	// Fetch allocation data function with pagination support
	const fetchAllocationData = useCallback(
		async (page = 1) => {
			if (!dateRange || !dateRange[0] || !dateRange[1]) return;

			const fromDate = dateRange[0].format('YYYY-MM-DD');
			const toDate = dateRange[1].format('YYYY-MM-DD');

			// One-Off mode: use one-off route
			if (isOneOffMode) {
				if (!oneOffConfigurations) {
					// No one-off configuration yet - don't fetch
					return;
				}

				dispatch(
					getOneOffExpenseAllocationAction({
						fromDate,
						toDate,
						oneOffConfigurations,
						page,
						limit: 20,
					})
				);
				return;
			}

			// Normal mode: use existing route with expense group
			if (!selectedExpenseGroup) return;

			// Get the selected expense group data
			const selectedExpenseGroupData = expenseGroups?.find(
				(group: any) => group.id === selectedExpenseGroup
			);

			// Check if temporaryConfig exists
			const hasTemporaryConfig =
				temporaryConfig &&
				temporaryConfig.expenseGroupId === selectedExpenseGroup;

			// Base payload - always includes required fields
			const params: any = {
				expenseGroupId: selectedExpenseGroup,
				fromDate,
				toDate,
				page,
				limit: 20,
			};

			// Determine tempFlag and build payload accordingly
			if (hasTemporaryConfig) {
				// tempFlag = true: Use temporary config data
				params.tempFlag = true;

				const tempAccounts = selectedExpenseGroupData?.accounts

				// Build tempConfigAllocations from temporaryConfig.items
				const tempConfigAllocations = (temporaryConfig.items || []).map(
					(item: any) => ({
						percentage: Number(item.percentage) || 0,
						customerId: item.customerId || null,
						customerName: item.customerName || null,
						classId: item.classId || null,
						className: item.className || null,
						locationId: item.locationId || null,
						locationName: item.locationName || null,
					})
				);

				if (tempConfigAllocations.length === 0) {
					console.error('Temporary allocation configurations are required');
					return;
				}

				params.tempAccounts = tempAccounts;
				params.tempConfigAllocations = tempConfigAllocations;
			} else {
				// tempFlag = false: Backend uses database data
				params.tempFlag = false;
				// No tempAccounts or tempConfigAllocations sent
				// Backend will use expenseGroup.accounts and expenseConfigAllocation from database
			}

			dispatch(getExpenseAllocationAction(params));
		},
		[
			selectedExpenseGroup,
			dateRange,
			dispatch,
			temporaryConfig,
			expenseGroups,
			isOneOffMode,
			oneOffConfigurations,
		]
	);

	// Load more allocations for infinite scroll
	const handleLoadMore = () => {
		if (hasMore && !isLoadingMore && !allocationLoading) {
			const nextPage = pagination.currentPage + 1;
			fetchAllocationData(nextPage);
		}
	};

	// Determine if allocations are configured for the current expense group
	const hasConfiguredAllocations = useMemo(() => {
		if (isOneOffMode) {
			// For One-Off mode, check if oneOffConfigurations exists and has expenseConfigAllocations
			return (
				oneOffConfigurations &&
				oneOffConfigurations.expenseConfigAllocations &&
				oneOffConfigurations.expenseConfigAllocations.length > 0
			);
		}
		return configuredAllocations && configuredAllocations.length > 0;
	}, [configuredAllocations, isOneOffMode, oneOffConfigurations]);

	// Check if temporaryConfig exists and has accounts but no data
	const isTemporaryConfigWithNoData = useMemo(() => {
		return (
			temporaryConfig &&
			temporaryConfig.expenseGroupId === selectedExpenseGroup &&
			temporaryConfig.configAccounts &&
			temporaryConfig.configAccounts.length > 0 &&
			(!allocationData || allocationData.length === 0) &&
			!allocationLoading
		);
	}, [
		temporaryConfig,
		selectedExpenseGroup,
		allocationData,
		allocationLoading,
	]);

	// Callback to refresh allocation configuration and data after saving
	const handleAllocationSaved = useCallback(() => {
		if (isOneOffMode) {
			// For One-Off mode, refetch allocation data with updated configurations
			dispatch(resetAllocationList());
			fetchAllocationData(1);
		} else {
			// For normal mode, fetch configuration and then refetch allocation data
			fetchAllocationConfiguration();
			// Refetch allocation data to get latest allocations based on updated configuration
			dispatch(resetAllocationList());
			fetchAllocationData(1);
		}
	}, [fetchAllocationConfiguration, isOneOffMode, dispatch, fetchAllocationData]);

	// Fetch allocation configuration when expense group changes (only for normal mode)
	useEffect(() => {
		if (!isOneOffMode) {
			fetchAllocationConfiguration();
		}
	}, [fetchAllocationConfiguration, isOneOffMode]);

	// When temporaryConfig is updated for the current expense group, refresh allocation data once (normal mode only)
	const lastAppliedTempRef = useRef<string | null>(null);
	useEffect(() => {
		if (
			isOneOffMode ||
			!temporaryConfig ||
			temporaryConfig.expenseGroupId !== selectedExpenseGroup ||
			!dateRange ||
			!dateRange[0] ||
			!dateRange[1]
		)
			return;

		const key = JSON.stringify({
			eg: temporaryConfig.expenseGroupId,
			accounts: temporaryConfig.configAccounts || [],
			singleMode: temporaryConfig.singleAccountMode ?? false,
			items: (temporaryConfig.items || []).map((i: any) => ({
				p: Number(i.percentage) || 0,
				c: i.customerId,
				cl: i.classId,
				l: i.locationId,
			})),
		});
		if (lastAppliedTempRef.current === key) return;
		lastAppliedTempRef.current = key;

		dispatch(resetAllocationList());
		fetchAllocationData(1);
	}, [
		temporaryConfig,
		selectedExpenseGroup,
		dateRange,
		dispatch,
		fetchAllocationData,
		isOneOffMode,
	]);

	// When oneOffConfigurations is updated, refresh allocation data (One-Off mode only)
	const lastAppliedOneOffRef = useRef<string | null>(null);
	useEffect(() => {
		if (
			!isOneOffMode ||
			!oneOffConfigurations ||
			!dateRange ||
			!dateRange[0] ||
			!dateRange[1]
		)
			return;

		const key = JSON.stringify({
			accounts: oneOffConfigurations.accountIds || [],
			customers: oneOffConfigurations.customerIds || [],
			classes: oneOffConfigurations.classIds || [],
			locations: oneOffConfigurations.locationIds || [],
			vendors: oneOffConfigurations.vendorIds || [],
			items: (oneOffConfigurations.expenseConfigAllocations || []).map((i: any) => ({
				p: Number(i.percentage) || 0,
				c: i.customerId,
				cl: i.classId,
				l: i.locationId,
			})),
		});
		if (lastAppliedOneOffRef.current === key) return;
		lastAppliedOneOffRef.current = key;

		dispatch(resetAllocationList());
		fetchAllocationData(1);
	}, [
		oneOffConfigurations,
		dateRange,
		dispatch,
		fetchAllocationData,
		isOneOffMode,
	]);

	// Fetch allocation data when expense group or date range changes
	useEffect(() => {
		if (dateRange && dateRange[0] && dateRange[1]) {
			if (isOneOffMode) {
				// One-Off mode: only fetch if oneOffConfigurations exists
				if (oneOffConfigurations) {
					dispatch(resetAllocationList());
					fetchAllocationData(1);
				} else {
					// Clear data when no one-off configuration
					dispatch(clearAllocationData());
				}
			} else if (selectedExpenseGroup && selectedExpenseGroup !== 'all') {
				// Normal mode: reset data and fetch first page when filters change
				dispatch(resetAllocationList());
				fetchAllocationData(1);
			} else {
				// Clear data when no valid selection
				dispatch(clearAllocationData());
			}
		} else {
			// Clear data when no valid date range
			dispatch(clearAllocationData());
		}
	}, [selectedExpenseGroup, dateRange, dispatch, fetchAllocationData, isOneOffMode, oneOffConfigurations]);

	return (
		<div className={styles['expense-allocation-main']}>
			<AllocationSearchAndFilter
				dateRange={dateRange}
				expenseGroupOptions={expenseGroupOptions}
				selectedExpenseGroup={selectedExpenseGroup}
				onExpenseGroupChange={onExpenseGroupChange}
				allocationData={allocationData}
				onAllocationSaved={handleAllocationSaved}
				onSwitchToJournalEntries={onSwitchToJournalEntries}
				isTemporaryConfigWithNoData={isTemporaryConfigWithNoData}
				hasConfiguredAllocations={hasConfiguredAllocations}
			/>

			<div
				className={styles['expense-allocation-main__breakdown']}
				style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
			>
				<Text strong>Allocation Breakdown</Text>

				{selectedExpenseGroup && (
					<Text
						type="secondary"
						style={{ display: 'flex', alignItems: 'center' }}
					>
						Showing allocations for:{' '}
						<Tooltip
							title={
								expenseGroupOptions.find(
									(e: any) => e.value === selectedExpenseGroup
								)?.label || '—'
							}
							placement="bottom"
							overlayClassName={styles['allocation-tooltip']}
							mouseEnterDelay={0.3}
							mouseLeaveDelay={0.1}
							zIndex={9999}
						>
							<span
								className={
									styles['expense-allocation-main__allocation-inline-ellipsis']
								}
								style={{ marginLeft: '4px' }} // small spacing after the colon
							>
								{(() => {
									const label =
										expenseGroupOptions.find(
											(e: any) => e.value === selectedExpenseGroup
										)?.label || '—';
									return label.length > 50
										? label.substring(0, 50) + '...'
										: label;
								})()}
							</span>
						</Tooltip>
					</Text>
				)}
			</div>

			<div className={styles['expense-allocation-main__table']}>
				<AllocationTable
					data={allocationData || []}
					hasMore={hasMore}
					isLoadingMore={isLoadingMore}
					onLoadMore={handleLoadMore}
					loading={allocationLoading}
					selectedExpenseGroup={selectedExpenseGroup}
					onConfigureAllocations={openSettings}
					hasConfiguredAllocations={hasConfiguredAllocations}
					isTemporaryConfigWithNoData={isTemporaryConfigWithNoData}
					grandTotal={grandTotal}
					oneOffConfigured={isOneOffMode ? hasConfiguredAllocations : undefined}
				/>
			</div>

			{isOneOffMode && (
				<OneOffSettingsModal
					isModalOpen={isOneOffSettingsOpen}
					handleCancel={closeOneOffSettings}
					onAllocationSaved={handleAllocationSaved}
				/>
			)}

			{!isOneOffMode && isSettingsOpen && (
				<ExpenseGroupSettingsModal
					isModalOpen={isSettingsOpen}
					handleCancel={closeSettings}
					expenseGroup={selectedForSettings}
					onAllocationSaved={handleAllocationSaved}
					isTemporaryMode={true}
				/>
			)}
		</div>
	);
}
